import { BasicButton, InputTextArea, Loading, Typography } from "@/components";
import useCaseMutation from "@/hooks/useCaseMutation";
import useMessageMutation from "@/hooks/useMessageMutation";
import useTeamMutation from "@/hooks/useTeamMutation";
import { LETTER_TYPE } from "@/libraries/constant";
import { isAxiosError } from "@/libraries/helper";
import { type CaseReferralFormFieldSchema, MESSAGE_TAG } from "@/schemas";
import type { Case } from "@/types";
import { Link, createFileRoute } from "@tanstack/react-router";
import omit from "lodash/omit";
import { generateUUID } from "pubnub";
import { useCallback, useEffect } from "react";
import {
	Controller,
	type SubmitHandler,
	useFormContext,
} from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/case/referrals/message")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const {
		watch,
		reset,
		control,
		handleSubmit,
		formState: { isSubmitting, isValid, isDirty },
	} = useFormContext<CaseReferralFormFieldSchema>();

	const channelName = watch("channelName");

	const { createLetter } = useCaseMutation();
	const { create } = useMessageMutation();
	const { assignCaseExternal } = useTeamMutation();

	const onSubmit = useCallback<SubmitHandler<CaseReferralFormFieldSchema>>(
		(data, e) => {
			e?.stopPropagation();
			e?.preventDefault();
			const textMessage = [
				data.team
					? data.team.users.map((member) => `@${member.fullname}`).join(", ")
					: `@${data.member.fullname}`,
				data.text,
			].join(" ");
			if (data.team) {
				return assignCaseExternal.mutateAsync(
					{
						id: data.team.id,
						caseId: data.caseId,
						message: {
							channel_name: data._case.channel_name,
							tags: data.tags,
							text: textMessage,
							id: generateUUID(),
						},
					},
					{
						onSuccess: async (res) => {
							if (res.message.tags === MESSAGE_TAG.REFERAL) {
								await createLetter.mutateAsync({
									caseId: data.caseId || data._case.id,
									// @ts-expect-error
									case_letter: {
										letter_type: LETTER_TYPE.REFERRAL_LETTER,
										summary: data.text,
									},
								});
							}
							const caseItem = data._case as Case;
							await navigate({
								replace: true,
								to: "/dashboard/case/referrals/success",
								search: {
									cid: data.caseId,
									tags:
										data.tags === MESSAGE_TAG.SPECIALISTS_ADVICE
											? "advice"
											: data.tags,
									ctitle: !caseItem.patient
										? caseItem.title
										: [caseItem.patient.name, caseItem.patient.surname].join(
												" ",
											),
									members: data.team.users
										.map((member) => member.fullname)
										.join(" & "),
								},
							});
							reset();
						},
						onError: (err) => {
							if (isAxiosError(err)) {
								return toast.error("Team member already join as case member.");
							}
							return toast.error("Something went wrong!");
						},
					},
				);
			}
			return create.mutateAsync(
				{
					id: generateUUID(),
					channel_name: data.channelName,
					tags: data.tags,
					mentions: [data.member.id],
					text: textMessage,
				},
				{
					onSuccess: async () => {
						const caseItem = data._case as Case;
						await navigate({
							replace: true,
							to: "/dashboard/case/referrals/success",
							search: {
								cid: data.caseId,
								tags:
									data.tags === MESSAGE_TAG.SPECIALISTS_ADVICE
										? "advice"
										: data.tags,
								ctitle: !caseItem.patient
									? caseItem.title
									: [caseItem.patient.name, caseItem.patient.surname].join(" "),
								members: data.member.fullname,
							},
						});
						reset();
					},
				},
			);
		},
		[create, assignCaseExternal, reset, navigate, createLetter],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!channelName) {
			navigate({
				replace: true,
				to: "/dashboard/case/referrals/contact",
			});
		}
	}, [channelName]);

	return (
		<>
			<div className="container flex flex-col gap-1">
				<Typography fontWeight={900} fontSize={14}>
					Type a message (Recommended)
				</Typography>
				<Typography fontWeight={400} fontSize={12}>
					Share relevant case details, current status, and what specific input
					you need.
				</Typography>
			</div>
			<div className="container flex-1 flex flex-col gap-3">
				<Controller
					control={control}
					name="text"
					defaultValue=""
					render={({ field, fieldState }) => (
						<InputTextArea
							fullWidth
							label="Message"
							placeholder="Type message"
							minRows={3}
							maxRows={6}
							maxLength={300}
							error={!!fieldState.error?.message}
							helperText={fieldState.error?.message}
							{...omit(field, ["ref"])}
						/>
					)}
				/>
			</div>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="container grid grid-cols-2 gap-2 bg-white pb-4"
			>
				<Link to="/dashboard/case/referrals/contact">
					<BasicButton
						variant="outlined"
						color="secondary"
						className="!h-12 text-sm font-bold !shadow-none"
						fullWidth
					>
						Back
					</BasicButton>
				</Link>
				<BasicButton
					type="submit"
					variant="contained"
					color="secondary"
					className="!h-12 text-sm font-bold !shadow-none"
					fullWidth
					disable={!isValid || !isDirty}
				>
					Invite
				</BasicButton>
			</form>
			<Loading status={isSubmitting} />
		</>
	);
}
